/**
 * WEBEXP — Predictive search with fuzzy matching + Shopify fallback
 *
 *  - Reads product catalog from <script id="webexp-product-catalog">
 *  - Scores against title / vendor / type / tags using prefix,
 *    substring, subsequence, and bounded Levenshtein
 *  - Renders top N matches into a results panel
 *  - If local catalog returns nothing, falls back to Shopify's
 *    /search/suggest.json so any product visible to Shopify's search
 *    index still shows up (handles catalog gaps + sales-channel quirks)
 *  - Handles arrow/Enter keyboard nav and outside-click dismissal
 *
 * Usage (called from each consuming section):
 *   WebexpSearch.attach({
 *     input:        HTMLInputElement,
 *     panel:        HTMLElement,           // results container
 *     maxResults:   6,
 *     minQueryLen:  1,
 *     onOpen?:      () => void,
 *     onClose?:     () => void,
 *   });
 */
(function () {
  'use strict';

  // ─── Catalog loading (cached, lazy) ───────────────────────────────────
  var _catalog = null;
  function getCatalog() {
    if (_catalog) return _catalog;
    var el = document.getElementById('webexp-product-catalog');
    if (!el) {
      _catalog = [];
      return _catalog;
    }
    try {
      _catalog = JSON.parse(el.textContent || '[]');
    } catch (err) {
      console.warn('[WebexpSearch] catalog parse failed', err);
      _catalog = [];
    }
    return _catalog;
  }

  // ─── Scoring primitives ───────────────────────────────────────────────
  function isSubsequence(needle, hay) {
    var i = 0;
    for (var j = 0; j < hay.length && i < needle.length; j++) {
      if (needle.charCodeAt(i) === hay.charCodeAt(j)) i++;
    }
    return i === needle.length;
  }

  function levenshtein(a, b, max) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    if (Math.abs(a.length - b.length) > (max || 99)) return (max || 99) + 1;

    var prev = new Array(b.length + 1);
    var curr = new Array(b.length + 1);
    for (var j = 0; j <= b.length; j++) prev[j] = j;

    for (var i = 1; i <= a.length; i++) {
      curr[0] = i;
      var rowMin = curr[0];
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(k - 1) ? 0 : 1;
        curr[k] = Math.min(prev[k] + 1, curr[k - 1] + 1, prev[k - 1] + cost);
        if (curr[k] < rowMin) rowMin = curr[k];
      }
      if (max != null && rowMin > max) return max + 1;
      for (var t = 0; t <= b.length; t++) prev[t] = curr[t];
    }
    return prev[b.length];
  }

  function scoreField(q, field) {
    if (!field) return 0;
    field = String(field).toLowerCase();
    if (field === q) return 110;
    if (field.startsWith(q)) return 100;

    var words = field.split(/[\s\-—_/,]+/).filter(Boolean);
    for (var i = 0; i < words.length; i++) {
      if (words[i].startsWith(q)) return 80;
    }

    if (field.indexOf(q) !== -1) return 60;

    // Subsequence — catches "drft" → "drift" (missing char)
    if (q.length >= 3 && isSubsequence(q, field)) return 45;

    // Bounded Levenshtein for transpositions / single-char swaps
    if (q.length >= 3) {
      var maxLev = q.length <= 4 ? 1 : 2;
      var lev = levenshtein(q, field, maxLev);
      if (lev <= maxLev) return 35 - lev * 5;
      for (var w = 0; w < words.length; w++) {
        var wlev = levenshtein(q, words[w], maxLev);
        if (wlev <= maxLev) return 30 - wlev * 5;
      }
    }
    return 0;
  }

  function scoreProduct(query, product) {
    var q = String(query || '').toLowerCase().trim();
    if (!q) return 0;
    var title = scoreField(q, product.title);
    var vendor = scoreField(q, product.vendor) * 0.5;
    var type = scoreField(q, product.type) * 0.5;
    var tags = scoreField(q, product.tags) * 0.4;
    return Math.max(title, vendor, type, tags);
  }

  function match(query, limit) {
    var q = String(query || '').toLowerCase().trim();
    if (!q) return [];
    var catalog = getCatalog();
    var scored = [];
    for (var i = 0; i < catalog.length; i++) {
      var s = scoreProduct(q, catalog[i]);
      if (s > 0) scored.push({ score: s, product: catalog[i] });
    }
    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.product.title.localeCompare(b.product.title);
    });
    return scored.slice(0, limit || 6).map(function (x) { return x.product; });
  }

  // ─── Shopify /search/suggest.json fallback ────────────────────────────
  function formatShopifyPrice(p) {
    if (p == null || p === '') return '';
    var s = String(p);
    // Already-formatted like "$10.00" or "10.00" → leave/normalize
    if (s.indexOf('$') === 0) return s;
    if (s.indexOf('.') !== -1) {
      var f = parseFloat(s);
      return isNaN(f) ? '' : '$' + f.toFixed(2);
    }
    // Plain cents like "10900"
    var n = parseInt(s, 10);
    if (isNaN(n)) return '';
    return '$' + (n / 100).toFixed(2);
  }

  function fetchShopifySuggest(query, limit, signal) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
              '&resources[type]=product&resources[limit]=' + (limit || 8);
    return fetch(url, { headers: { 'Accept': 'application/json' }, signal: signal })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.resources &&
                        data.resources.results &&
                        data.resources.results.products) || [];
        return products.map(function (p) {
          return {
            title: p.title || '',
            handle: p.handle || '',
            url: p.url || ('/products/' + (p.handle || '')),
            img: p.image ||
                 (p.featured_image && (p.featured_image.url || p.featured_image)) ||
                 '',
            price: formatShopifyPrice(p.price_min != null ? p.price_min : p.price),
            vendor: p.vendor || '',
            type: p.product_type || '',
            tags: ''
          };
        });
      });
  }

  // ─── Rendering ────────────────────────────────────────────────────────
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderLoading(panel) {
    panel.innerHTML = '<span class="webexp-psearch__empty">Searching…</span>';
  }

  function renderResults(panel, products, query) {
    if (!products.length) {
      panel.innerHTML =
        '<span class="webexp-psearch__empty">' +
          'No matches for "' + escapeHtml(query) + '"' +
        '</span>';
      return;
    }
    var html = '<span class="webexp-psearch__list" role="listbox">';
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var imgWrap = p.img
        ? '<span class="webexp-psearch__img-wrap">' +
            '<img class="webexp-psearch__img" src="' + escapeHtml(p.img) + '" ' +
              'alt="' + escapeHtml(p.title) + '" loading="lazy" width="56" height="56">' +
          '</span>'
        : '<span class="webexp-psearch__img-wrap webexp-psearch__img-wrap--empty"></span>';
      html +=
        '<a href="' + escapeHtml(p.url) + '" class="webexp-psearch__item" ' +
            'role="option" data-psearch-item tabindex="-1">' +
          imgWrap +
          '<span class="webexp-psearch__meta">' +
            '<span class="webexp-psearch__title">' + escapeHtml(p.title) + '</span>' +
            '<span class="webexp-psearch__price">' + escapeHtml(p.price) + '</span>' +
          '</span>' +
        '</a>';
    }
    html += '</span>';
    panel.innerHTML = html;
  }

  /**
   * Merge local + Shopify-suggest results.
   * - Dedupe by URL
   * - Re-score using our local algorithm so the ranking is consistent
   * - Server-only entries get a sensible floor score so high-quality
   *   server matches (e.g. Shopify-indexed text matches we don\'t see
   *   locally) still show up
   */
  function mergeAndRank(query, localList, serverList, limit) {
    var seen = Object.create(null);
    var pool = [];

    function add(product, isLocal) {
      if (!product) return;
      // Prefer handle (stable across sources); fall back to URL minus query/hash
      var key = product.handle && String(product.handle).toLowerCase();
      if (!key) {
        var u = String(product.url || '');
        key = u.split('?')[0].split('#')[0].toLowerCase();
      }
      if (!key) return;
      if (seen[key]) {
        // already added — boost score if it appears in both sources
        seen[key].score += 5;
        return;
      }
      var s = scoreProduct(query, product);
      // Server-only result we couldn\'t match on our fields → trust Shopify
      // and give it a floor so it still appears (between substring + subseq).
      if (s === 0 && !isLocal) s = 50;
      var entry = { product: product, score: s, local: isLocal };
      seen[key] = entry;
      pool.push(entry);
    }

    for (var i = 0; i < localList.length; i++) add(localList[i], true);
    for (var j = 0; j < serverList.length; j++) add(serverList[j], false);

    pool.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.product.title.localeCompare(b.product.title);
    });

    return pool.slice(0, limit || 6).map(function (x) { return x.product; });
  }

  // ─── Attaching to an input + panel ────────────────────────────────────
  function attach(opts) {
    var input = opts.input;
    var panel = opts.panel;
    if (!input || !panel) return;
    var maxResults = opts.maxResults || 6;
    var minQ = opts.minQueryLen || 1;
    var debounceMs = opts.debounceMs == null ? 80 : opts.debounceMs;
    var activeIdx = -1;
    var currentAbort = null;

    function open() {
      if (panel.dataset.open === 'true') return;
      panel.dataset.open = 'true';
      panel.removeAttribute('hidden');
      input.setAttribute('aria-expanded', 'true');
      if (typeof opts.onOpen === 'function') opts.onOpen();
    }

    function close() {
      if (panel.dataset.open !== 'true') return;
      panel.dataset.open = 'false';
      panel.setAttribute('hidden', '');
      input.setAttribute('aria-expanded', 'false');
      activeIdx = -1;
      if (currentAbort) {
        try { currentAbort.abort(); } catch (_) {}
        currentAbort = null;
      }
      if (typeof opts.onClose === 'function') opts.onClose();
    }

    function items() {
      return panel.querySelectorAll('[data-psearch-item]');
    }

    function setActive(idx) {
      var all = items();
      if (!all.length) { activeIdx = -1; return; }
      activeIdx = ((idx % all.length) + all.length) % all.length;
      for (var i = 0; i < all.length; i++) {
        if (i === activeIdx) {
          all[i].classList.add('is-active');
          all[i].setAttribute('aria-selected', 'true');
        } else {
          all[i].classList.remove('is-active');
          all[i].removeAttribute('aria-selected');
        }
      }
    }

    /**
     * Always run local fuzzy + Shopify suggest in parallel, merge by URL,
     * re-score, and show the top N.  Local results render immediately so
     * the panel feels instant; server results merge in when they arrive.
     */
    function run() {
      var q = input.value;
      if (!q || q.length < minQ) {
        close();
        return;
      }

      // Pull a generous local pool so merging has room
      var localResults = match(q, maxResults * 2);

      // Paint local results right away (instant feel)
      if (localResults.length > 0) {
        renderResults(panel, localResults.slice(0, maxResults), q);
        open();
      } else {
        renderLoading(panel);
        open();
      }
      activeIdx = -1;

      // Always hit Shopify suggest in parallel — covers products that
      // are missing from the local catalog snippet (sales-channel quirks,
      // products added since last full-page render, etc.)
      if (currentAbort) {
        try { currentAbort.abort(); } catch (_) {}
      }
      try {
        currentAbort = ('AbortController' in window) ? new AbortController() : null;
      } catch (_) {
        currentAbort = null;
      }
      fetchShopifySuggest(q, maxResults * 2, currentAbort ? currentAbort.signal : undefined)
        .then(function (apiResults) {
          if (input.value !== q) return;
          var merged = mergeAndRank(q, localResults, apiResults, maxResults);
          renderResults(panel, merged, q);
          activeIdx = -1;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Server failed — if we already showed local, leave it; otherwise empty
          if (input.value !== q) return;
          if (localResults.length === 0) {
            renderResults(panel, [], q);
          }
        });
    }

    var debounceTimer;
    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(run, debounceMs);
    });

    input.addEventListener('focus', function () {
      if (input.value && input.value.length >= minQ) run();
    });

    input.addEventListener('keydown', function (e) {
      var all = items();
      if (e.key === 'ArrowDown') {
        if (!all.length) return;
        e.preventDefault();
        setActive(activeIdx + 1);
      } else if (e.key === 'ArrowUp') {
        if (!all.length) return;
        e.preventDefault();
        setActive(activeIdx - 1);
      } else if (e.key === 'Enter') {
        if (activeIdx >= 0 && all[activeIdx]) {
          e.preventDefault();
          window.location.href = all[activeIdx].getAttribute('href');
        }
        // else: let the form submit naturally to /search?q=…
      } else if (e.key === 'Escape') {
        if (panel.dataset.open === 'true') {
          e.preventDefault();
          close();
        }
      }
    });

    document.addEventListener('click', function (e) {
      if (panel.dataset.open !== 'true') return;
      if (panel.contains(e.target) || input.contains(e.target)) return;
      var wrapper = e.target.closest('[data-psearch-keep-open]');
      if (wrapper && wrapper.contains(input)) return;
      close();
    });

    return { open: open, close: close, refresh: run };
  }

  // ─── Public API ───────────────────────────────────────────────────────
  window.WebexpSearch = {
    attach: attach,
    match: match,
    fetchShopifySuggest: fetchShopifySuggest,
    _internal: { scoreProduct: scoreProduct, getCatalog: getCatalog }
  };
})();
